import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreVertical, Trash2, Copy, ExternalLink, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RestaurantMedia } from '@/api/media';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

interface MediaCardProps {
  media: RestaurantMedia;
  onDelete?: (mediaId: string) => void;
  onSelect?: (media: RestaurantMedia) => void;
  selected?: boolean;
  showActions?: boolean;
}

export const MediaCard: React.FC<MediaCardProps> = ({
  media,
  onDelete,
  onSelect,
  selected,
  showActions = true
}) => {
  const copyUrl = () => {
    navigator.clipboard.writeText(media.public_url);
    toast({
      title: "URL copied",
      description: "Image URL copied to clipboard"
    });
  };

  return (
    <Card
      className={cn(
        "group relative cursor-pointer hover:border-primary transition-colors",
        selected && "border-primary border-2"
      )}
      onClick={() => onSelect?.(media)}
    >
      <CardContent className="p-2">
        <div className="relative aspect-square">
          <img
            src={media.thumbnail_url || media.public_url}
            alt={media.original_filename || 'Media'}
            className="w-full h-full object-cover rounded"
          />
          {showActions && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyUrl(); }}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy URL
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(media.public_url, '_blank'); }}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open in New Tab
                  </DropdownMenuItem>
                  {onDelete && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(media.id);
                      }}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <p className="text-xs font-medium truncate">{media.original_filename || 'Untitled'}</p>
          <div className="flex gap-1 flex-wrap">
            <Badge variant="secondary" className="text-xs flex items-center gap-1">
              {media.media_type === 'video' ? (
                <>
                  <VideoIcon className="h-3 w-3" />
                  Video
                </>
              ) : (
                <>
                  <ImageIcon className="h-3 w-3" />
                  Image
                </>
              )}
            </Badge>
            <Badge variant={media.source_type === 'uploaded' ? 'outline' : 'default'} className="text-xs">
              {media.source_type === 'uploaded' ? 'Uploaded' : 'AI'}
            </Badge>
            {media.tags?.slice(0, 1).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
            ))}
            {media.tags && media.tags.length > 1 && (
              <Badge variant="outline" className="text-xs">+{media.tags.length - 1}</Badge>
            )}
          </div>
          {media.width && media.height && media.file_size && (
            <p className="text-xs text-muted-foreground">
              {media.width} × {media.height} • {(media.file_size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
